import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Group,
  Stack,
  Table,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowsSort,
  Settings,
  SortAscending2,
  SortDescending2,
} from "tabler-icons-react";
import {
  useTable,
  useSortBy,
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useFilters,
} from "react-table";
import { useParams } from "react-router-dom";
import userService from "../../api/userService";
import logService from "../../api/logService";
import { showNotification } from "@mantine/notifications";
import { User } from "../../model/User";
import AppLoadingOverlay from "../../components/AppLoadingOverlay";
import NewAccountForm from "../../components/NewAccountForm";

interface CompanyAccountDetailProps {}

const CompanyAccountDetail: React.FC<CompanyAccountDetailProps> = ({}) => {
  const theme = useMantineTheme();
  const [form, setForm] = useState(false);
  const [userInfo, setUserInfo] = useState<User>();
  const [data, setData] = useState<
    { name: string; deviceIMEI: string; _id?: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    getData();
  }, [id]);

  const getData = async () => {
    setLoading(true);
    try {
      const result = await userService.getUserDetail(id);
      setData(result.data.vehicles);
      setUserInfo(result.data);
      setLoading(false);
    } catch (ex) {
      showNotification({
        title: "Something went wrong",
        message: "Plesase try again",
        color: "red",
      });
      logService.log(ex);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        sortType: "basic",
      },
      {
        Header: "Device IMEI",
        accessor: "deviceIMEI",
        sortType: "basic",
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <Box style={{ position: "relative", minHeight: 400 }}>
      <AppLoadingOverlay loading={loading} />
      {data && userInfo && (
        <>
          <NewAccountForm
            visible={form}
            onClose={() => setForm(false)}
            initialData={userInfo}
            onUpdate={() => {
              window.location.reload();
            }}
            // onUpdate={(user: User) => {
            //   setUserInfo(user);
            //   // @ts-expect-error
            //   setData(user.vehicles);
            // }}
            companyId={userInfo.company}
          />
          <Group position="apart" noWrap align={"flex-start"}>
            <Title order={3}>{userInfo.userName}</Title>

            <ActionIcon radius="xl" onClick={() => setForm(true)}>
              <Settings size={20} />
            </ActionIcon>
          </Group>

          <Stack
            spacing={"xs"}
            mt="xl"
            mb="xs"
            p="md"
            style={{
              border: `1px solid ${theme.colors.border[0]}`,
              borderRadius: theme.radius.md,
            }}
          >
            <Group position="apart">
              <Text size="sm">Email</Text>
              <Text size="sm" weight={"bold"}>
                {userInfo.email}
              </Text>
            </Group>
            <Group position="apart">
              <Text size="sm">Username</Text>
              <Text size="sm" weight={"bold"}>
                {userInfo.userName}
              </Text>
            </Group>
            <Group position="apart" align={"flex-start"}>
              <Text size="sm">Phones</Text>
              <Stack spacing={0}>
                {userInfo.phones.length === 0 && (
                  <Text size="sm" weight={"bold"}>
                    No data
                  </Text>
                )}
                {userInfo.phones.map((item) => (
                  <Text size="sm" weight={"bold"} key={item}>
                    {item}
                  </Text>
                ))}
              </Stack>
            </Group>
            <Group position="apart">
              <Text size="sm">Roles</Text>
              <Text size="sm" weight={"bold"}>
                {userInfo.roles &&
                  userInfo.roles.map((r) => (
                    <Badge key={r} color={r === "admin" ? "green" : "indigo"}>
                      {r}
                    </Badge>
                  ))}
              </Text>
            </Group>
          </Stack>

          <Title order={5} pt="xl" mb="sm">
            Assigned vehicles{" "}
            <span style={{ fontWeight: 400 }}>{`(${data.length})`}</span>
          </Title>

          <div
            style={{
              maxHeight: "calc(100vh - 380px)",
              overflow: "scroll",
              border: `1px solid ${theme.colors.border[0]}`,
              borderRadius: 5,
            }}
          >
            <Table highlightOnHover>
              <table {...getTableProps()} style={{ width: "100%" }}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: theme.colors.background[1],
                      }}
                    >
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          <Group>
                            {column.render("Header")}

                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <SortDescending2
                                  size={15}
                                  color={
                                    column.isSorted
                                      ? theme.colors.secondary[0]
                                      : theme.colors.white[0]
                                  }
                                />
                              ) : (
                                <SortAscending2
                                  size={15}
                                  color={
                                    column.isSorted
                                      ? theme.colors.secondary[0]
                                      : theme.colors.white[0]
                                  }
                                />
                              )
                            ) : (
                              <ArrowsSort size={15} />
                            )}
                          </Group>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.length === 0 ? (
                    <Text p="md">No vehicle data</Text>
                  ) : (
                    rows.map((row, i) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => {
                            return (
                              <td {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </Table>
          </div>
        </>
      )}
    </Box>
  );
};

export default CompanyAccountDetail;
